import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IProduto } from "app/types/produto/IProduto";

export type ProdutosSelecionadosRedux = {
    produtos: IProduto[]
    modalCestaProdutosAberto: boolean
    // 482572 — timestamp (Date.now()) da última mudança real na cesta, gravado por
    // app/redux/store/store.ts sempre que `produtos` muda — persistido junto (mesma chave por
    // aba, ver produtosSelecionadosPersistedReducer em store.ts), usado só para expirar a cesta
    // órfã de outra aba após QUATRO_DIAS_EM_MS (ver useRecuperarCestaProdutosAnterior.ts).
    AtualizadoEm?: number
}

const initialState: ProdutosSelecionadosRedux = {
    produtos: [],
    modalCestaProdutosAberto: false,
}

// 491123 — cada linha da cesta é identificada por IdentificadorLinhaCesta quando presente
// (necessário para distinguir duplicatas do mesmo produto, ver onInserirProdutoDuplicadoSelecionado
// em PaginaPesquisaProdutoContainer.tsx), caindo para Codigo quando ausente (produto "normal",
// sem duplicata — para esses, IdentificadorLinhaCesta == Codigo). Nunca usar Codigo sozinho aqui:
// ele é o código real do produto (enviado à API) e pode ser igual entre duas linhas distintas.
const obterIdentificadorLinha = (produto: IProduto) => produto.IdentificadorLinhaCesta ?? produto.Codigo

const produtosSelecionadosSlice = createSlice({
    name: "produtosSelecionados",
    initialState,
    // 491123 — NÃO escuta `resetAll` (disparado em todo login, ver LoginContainer.tsx): antes era
    // inofensivo, pois a cesta só existia em sessionStorage (já vazio após um fechamento real do
    // navegador). Agora que ela é persistida em localStorage justamente para sobreviver a esse
    // fechamento (ver produtosSelecionadosPersistedReducer em store.ts), resetAll no login
    // apagaria a sacola ANTES do hook de recuperação (useRecuperarCestaProdutosAnterior.ts) rodar
    // — exatamente o cenário real, já que reabrir o navegador exige logar de novo.
    //
    // NÃO escuta mais `orcamentoSlice.actions.limparInformacoesOrcamento` (removido nesta ficha):
    // essa action é disparada em vários pontos que não devem necessariamente esvaziar a cesta. A
    // limpeza explícita (`limparProdutosSelecionadosAction`) é disparada só nos pontos que
    // representam "saiu de verdade" de uma Venda/Outras Saídas/Transferência em andamento:
    // concluído (orcamentoConcluidoContainer.tsx, ao ENTRAR na tela), abandono confirmado ao
    // navegar para outro menu OU para um novo tipo de orçamento (Venda/Outras Saídas/Transferência
    // — MenuPrincipalContainer.confirmarAbandonoDoOrcamento) ou logout com orçamento em andamento
    // (PerfilButtonContainer.fazerLogout).
    reducers: {
        selecionarProdutos: (state, action: PayloadAction<IProduto[]>) => {
            // normaliza: todo produto armazenado na cesta passa a ter IdentificadorLinhaCesta
            // preenchido (defaultando para o próprio Codigo quando o chamador não definiu um)
            const produtosNovos = action.payload.map(produto => ({
                ...produto,
                IdentificadorLinhaCesta: obterIdentificadorLinha(produto),
            }))
            const produtosExistentes = state.produtos.filter(
                produtoExistente => !produtosNovos.some(produtoNovo => obterIdentificadorLinha(produtoNovo) == obterIdentificadorLinha(produtoExistente))
            )
            state.produtos = [...produtosExistentes, ...produtosNovos]
        },
        removerProduto: (state, action: PayloadAction<string>) => {
            state.produtos = state.produtos.filter(produto => obterIdentificadorLinha(produto) != action.payload)
            // 491123 — o Dialog da cesta só abre com produtos.length > 0 (ver open= em
            // PaginaPesquisaProduto.tsx); se a remoção esvaziou a cesta, o Dialog já fecha
            // visualmente por causa disso, mas modalCestaProdutosAberto ficava preso em true.
            // Sem fechar aqui, adicionar QUALQUER produto depois reabria o modal sozinho,
            // sem o usuário ter clicado no botão flutuante.
            if (state.produtos.length === 0) {
                state.modalCestaProdutosAberto = false
            }
        },
        // 491123 — usado pelo botão de remover da GRADE de pesquisa (itemPesquisaProduto_BotaoDeleteCesta_),
        // que não representa uma linha específica da cesta (ao contrário de removerProduto, usado pela
        // própria TabelaProdutosSelecionados) — remove TODAS as linhas/duplicatas do mesmo produto de
        // uma vez, por CodigoProduto, não por IdentificadorLinhaCesta.
        removerTodasDuplicatasPorCodigoProduto: (state, action: PayloadAction<string>) => {
            state.produtos = state.produtos.filter(produto => produto.CodigoProduto != action.payload)
            if (state.produtos.length === 0) {
                state.modalCestaProdutosAberto = false
            }
        },
        atualizarQuantidade: (state, action: PayloadAction<{ codigo: string, quantidade: number }>) => {
            state.produtos = state.produtos.map(produto =>
                obterIdentificadorLinha(produto) == action.payload.codigo ? { ...produto, Quantidade: action.payload.quantidade } : produto
            )
        },
        atualizarAcaoProdutoRepetido: (state, action: PayloadAction<{ codigo: string, acao: number }>) => {
            state.produtos = state.produtos.map(produto =>
                obterIdentificadorLinha(produto) == action.payload.codigo ? { ...produto, AcaoProdutoRepetido: action.payload.acao } : produto
            )
        },
        atualizarCorTinta: (state, action: PayloadAction<{ codigo: string, corTinta: IProduto["CorTinta"], preco: number, repetido: boolean }>) => {
            state.produtos = state.produtos.map(produto =>
                obterIdentificadorLinha(produto) == action.payload.codigo
                    ? { ...produto, CorTinta: action.payload.corTinta, Configurado: true, Preco: action.payload.preco, Repetido: action.payload.repetido }
                    : produto
            )
        },
        limparCorTinta: (state, action: PayloadAction<string>) => {
            state.produtos = state.produtos.map(produto =>
                obterIdentificadorLinha(produto) == action.payload
                    ? { ...produto, CorTinta: null, Configurado: null }
                    : produto
            )
        },
        limparProdutosSelecionados: (state) => {
            state.produtos = []
            // 491123 — mesmo motivo do removerProduto: cesta vazia não deve deixar o modal
            // "logicamente aberto" (modalCestaProdutosAberto=true) para não reabrir sozinho
            // no próximo produto selecionado.
            state.modalCestaProdutosAberto = false
        },
        abrirModalCestaProdutos: (state) => {
            state.modalCestaProdutosAberto = true
        },
        fecharModalCestaProdutos: (state) => {
            state.modalCestaProdutosAberto = false
        },
        // 482572 — ver AtualizadoEm acima; despachada de app/redux/store/store.ts (não pelos
        // reducers de conteúdo acima) para não precisar repetir `state.AtualizadoEm = ...` em cada
        // um deles.
        marcarProdutosSelecionadosAtualizado: (state, action: PayloadAction<number>) => {
            state.AtualizadoEm = action.payload
        }
    }
})

export default produtosSelecionadosSlice.reducer

export const {
    selecionarProdutos: selecionarProdutosAction,
    removerProduto: removerProdutoSelecionadoAction,
    removerTodasDuplicatasPorCodigoProduto: removerTodasDuplicatasProdutoSelecionadoAction,
    atualizarQuantidade: atualizarQuantidadeProdutoSelecionadoAction,
    atualizarAcaoProdutoRepetido: atualizarAcaoProdutoRepetidoSelecionadoAction,
    atualizarCorTinta: atualizarCorTintaProdutoSelecionadoAction,
    limparCorTinta: limparCorTintaProdutoSelecionadoAction,
    limparProdutosSelecionados: limparProdutosSelecionadosAction,
    abrirModalCestaProdutos: abrirModalCestaProdutosAction,
    fecharModalCestaProdutos: fecharModalCestaProdutosAction,
    marcarProdutosSelecionadosAtualizado: marcarProdutosSelecionadosAtualizadoAction
} = produtosSelecionadosSlice.actions
